import { getLogger as baseGetLogger } from '@logtape/logtape';

export type LogMeta = Record<string, unknown>;

const RESERVED_TOP_LEVEL_NAMES: ReadonlySet<string> = new Set([
  'context', 'duration', 'env', 'http', 'level', 'message', 'request_id',
  'service', 'session_id', 'span_id', 'timestamp', 'trace_id', 'user', 'version',
  'date', 'logger', 'origin', 'session', 'status', 'usr', 'view',
]);

export function findReservedNames(meta: Record<string, unknown>): string[] {
  return Object.keys(meta).filter((key) => RESERVED_TOP_LEVEL_NAMES.has(key));
}

function stripReservedNames(meta: Record<string, unknown>): Record<string, unknown> {
  const domain: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(meta)) {
    if (!RESERVED_TOP_LEVEL_NAMES.has(key)) {
      domain[key] = value;
    }
  }

  return domain;
}

function assertNoReservedNames(meta: Record<string, unknown>): void {
  if (process.env.NODE_ENV === 'production') {
    return;
  }

  const reserved = findReservedNames(meta);

  if (reserved.length > 0) {
    throw new Error(
      `Log meta may not carry reserved top-level names: ${reserved.join(', ')}. `
      + 'Move them into an object named after the domain.',
    );
  }
}

function serializeError(error: unknown) {
  if (error instanceof Error) {
    return { kind: error.name, message: error.message, stack: error.stack };
  }

  return { kind: 'NonErrorThrown', message: String(error) };
}

function buildProperties(meta?: unknown): Record<string, unknown> {
  const metaIsError = meta instanceof Error;
  const isObject = typeof meta === 'object' && meta !== null && !metaIsError;
  const declared: Record<string, unknown> = isObject
    ? { ...(meta as Record<string, unknown>) }
    : {};
  const errorValue = metaIsError ? meta : declared.error;

  delete declared.error;
  assertNoReservedNames(declared);

  const properties = stripReservedNames(declared);

  if (errorValue !== undefined) {
    properties.error = serializeError(errorValue);
  }

  return properties;
}

export function getLogger(context?: string) {
  const logger = baseGetLogger(context ? ['app', context] : ['app']);

  return {
    error(message: string, meta?: LogMeta) {
      logger.error(message, buildProperties(meta));
    },

    info(message: string, meta?: LogMeta) {
      logger.info(message, buildProperties(meta));
    },

    warn(message: string, meta?: LogMeta) {
      logger.warn(message, buildProperties(meta));
    },

    debug(message: string, meta?: LogMeta) {
      logger.debug(message, buildProperties(meta));
    },
  };
}
